import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api, type Lane, type TaskRow } from "../lib/api";
import { fmtAgo } from "../lib/format";

const LANES: { key: Lane; label: string }[] = [
  { key: "idea", label: "Ideas" },
  { key: "todo", label: "To do" },
  { key: "doing", label: "Doing" },
  { key: "done", label: "Done" },
];

export function Board() {
  const qc = useQueryClient();
  const tasks = useQuery({ queryKey: ["tasks"], queryFn: api.tasks, refetchInterval: 20_000 });
  const invalidate = () => qc.invalidateQueries({ queryKey: ["tasks"] });
  const [error, setError] = useState<string | null>(null);
  const onError = (e: unknown) => setError(e instanceof Error ? e.message : String(e));
  const onSuccess = () => {
    setError(null);
    invalidate();
  };
  const create = useMutation({ mutationFn: api.taskCreate, onSuccess, onError });
  const update = useMutation({ mutationFn: ({ id, patch }: { id: number; patch: Parameters<typeof api.taskUpdate>[1] }) => api.taskUpdate(id, patch), onSuccess, onError });
  const remove = useMutation({ mutationFn: api.taskDelete, onSuccess, onError });
  const [dragId, setDragId] = useState<number | null>(null);

  const byLane = (lane: Lane) => (tasks.data ?? []).filter((t) => t.lane === lane).sort((a, b) => a.pos - b.pos);

  /** Drop onto a card (insert above it) or onto the lane body (append). */
  const drop = (lane: Lane, beforeId: number | null) => {
    if (dragId == null || dragId === beforeId) return;
    const cards = byLane(lane);
    const idx = beforeId == null ? cards.length : cards.findIndex((c) => c.id === beforeId);
    const above = idx > 0 ? cards[idx - 1] : null;
    update.mutate({ id: dragId, patch: { lane, before: beforeId, after: above && above.id !== dragId ? above.id : null } });
    setDragId(null);
  };

  return (
    <div className="page">
      <div className="page-head">
        <h2>Board</h2>
        <span className="muted">{tasks.data?.length ?? 0} cards · dev notes, tasks, ideas</span>
      </div>
      {(error || tasks.error) && (
        <div className="error-bar" role="alert">
          {error ?? String(tasks.error)}
          {error?.includes("404") && " — the server doesn't know /api/tasks yet; restart it (dev container: docker compose restart)"}
          <button className="small" style={{ marginLeft: 12 }} onClick={() => setError(null)}>
            dismiss
          </button>
        </div>
      )}
      <div className="board">
        {LANES.map(({ key, label }) => (
          <div
            key={key}
            className="lane"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              drop(key, null);
            }}
          >
            <h3>
              {label} <span className="muted">{byLane(key).length}</span>
            </h3>
            {byLane(key).map((t) => (
              <Card
                key={t.id}
                t={t}
                dragging={dragId === t.id}
                onDragStart={() => setDragId(t.id)}
                onDragEnd={() => setDragId(null)}
                onDropBefore={() => drop(key, t.id)}
                onSave={(patch) => update.mutate({ id: t.id, patch })}
                onDelete={() => remove.mutate(t.id)}
              />
            ))}
            <AddCard onAdd={(title) => create.mutate({ title, lane: key })} />
          </div>
        ))}
      </div>
    </div>
  );
}

function Card({ t, dragging, onDragStart, onDragEnd, onDropBefore, onSave, onDelete }: {
  t: TaskRow;
  dragging: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
  onDropBefore: () => void;
  onSave: (patch: { title?: string; note?: string }) => void;
  onDelete: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(t.title);
  const [note, setNote] = useState(t.note ?? "");
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (editing) {
    return (
      <div className="card editing">
        <input value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
        <textarea value={note} placeholder="note (optional)" onChange={(e) => setNote(e.target.value)} rows={3} />
        <div className="row">
          <button
            className="small primary"
            onClick={() => {
              if (title.trim()) onSave({ title: title.trim(), note });
              setEditing(false);
            }}
          >
            save
          </button>
          <button className="small" onClick={() => { setEditing(false); setTitle(t.title); setNote(t.note ?? ""); }}>cancel</button>
          <span style={{ flex: 1 }} />
          {confirmDelete ? (
            <button className="small danger" onClick={onDelete}>really delete</button>
          ) : (
            <button className="small" onClick={() => setConfirmDelete(true)}>delete</button>
          )}
        </div>
      </div>
    );
  }
  return (
    <div
      className={`card${dragging ? " dragging" : ""}`}
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onDropBefore();
      }}
      onDoubleClick={() => setEditing(true)}
      title="double-click to edit · drag to move"
    >
      <div className="card-title">{t.title}</div>
      {t.note && <div className="card-note">{t.note}</div>}
      <div className="card-meta muted">{fmtAgo(t.updated_ts)}</div>
    </div>
  );
}

function AddCard({ onAdd }: { onAdd: (title: string) => void }) {
  const [text, setText] = useState("");
  return (
    <input
      className="add-card"
      value={text}
      placeholder="+ add"
      onChange={(e) => setText(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter" && text.trim()) {
          onAdd(text.trim());
          setText("");
        }
      }}
    />
  );
}
